import { getDisplayName } from './display-name';
import type { CIType, CITypeListItem } from '@/entities/srm/ci/@x/tql-query';
import type { TQLFilter } from '@/entities/srm/tql-query';

/** Prefix of the node identifier as it is printed in the UI. Not translated: it
 * is the same technical token in every locale, like the `ID` in `CopyIdBadge`. */
export const TQL_NODE_ID_PREFIX = 'ID:';

/** Separator between the node name and its identifier in a single-line label. */
const LABEL_SEPARATOR = ' · ';

export type CITypeLike = CIType | CITypeListItem;
export type CITypeLookup = Map<string, CITypeLike>;

/**
 * Why a node could not be named. The two failures are kept apart because they
 * are repaired in different places: `unknown-ci-type` in the CI type registry,
 * `not-in-query` in the TQL of the monitoring template itself.
 */
export type TQLNodeStatus = 'not-in-query' | 'resolved' | 'unknown-ci-type';

export interface TQLNodeInfo {
  /** `filterId` of the TQL node, `node_id` in the monitoring API. */
  nodeId: string;
  status: TQLNodeStatus;
  /** Human-readable name; `null` unless `status === 'resolved'`. */
  displayName: null | string;
  /** CI type system name — the `<ci_type>_<node_id>` part of a Jinja reference. */
  ciTypeName?: string;
}

/** Name shown instead of a missing one; the two failures read differently. */
export function getTQLNodeFallbackKey(node: TQLNodeInfo): string {
  return node.status === 'not-in-query'
    ? 'hiTab.items.removedNodeLabel'
    : 'hiTab.items.undefinedLabel';
}

/** Node reference as it is written in Jinja templates: `[[host_2.attributes.name]]`. */
export function getTQLNodeReference(node: TQLNodeInfo): string {
  return node.ciTypeName
    ? `${node.ciTypeName}_${node.nodeId}`
    : `_${node.nodeId}`;
}

/**
 * Resolve a `node_id` against the TQL of a monitoring template.
 *
 * `ciTypes` is treated as "not loaded yet" while empty, so a node is never
 * reported as broken just because the CI type registry has not arrived.
 */
export function resolveTQLNode(
  nodeId: null | number | string | undefined,
  query: TQLFilter | undefined,
  ciTypes: CITypeLookup,
): TQLNodeInfo {
  const id = String(nodeId ?? '');
  const data = query?.nodes?.find(
    (node) => String(node.data?.filterId) === id,
  )?.data;

  if (!data) {
    return { nodeId: id, status: 'not-in-query', displayName: null };
  }

  const ciTypeName = data.name as string | undefined;
  const cit = ciTypeName ? ciTypes.get(ciTypeName) : undefined;

  if (!cit && ciTypes.size > 0) {
    return {
      nodeId: id,
      status: 'unknown-ci-type',
      displayName: null,
      ciTypeName,
    };
  }

  return {
    nodeId: id,
    status: 'resolved',
    displayName: getDisplayName(data, cit) || null,
    ciTypeName,
  };
}

/** Every node of the TQL, in the order it is stored in the template. */
export function resolveTQLNodes(
  query: TQLFilter | undefined,
  ciTypes: CITypeLookup,
): TQLNodeInfo[] {
  return (query?.nodes ?? []).map((node) =>
    resolveTQLNode(node.data?.filterId as string, query, ciTypes),
  );
}

/**
 * Single-line label — the form the identifier has to survive in: Mantine puts
 * the plain `label` string into the closed input and into `MultiSelect` pills,
 * and filters by it, so the identifier cannot live in `renderOption` alone.
 */
export function formatTQLNodeLabel(
  node: TQLNodeInfo,
  fallbackName: string,
  withId = true,
): string {
  const name = node.displayName || fallbackName;
  return withId
    ? `${name}${LABEL_SEPARATOR}${TQL_NODE_ID_PREFIX} ${node.nodeId}`
    : name;
}
